import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Minus, Trash2, Package, Truck, ChevronDown } from 'lucide-react';
import Input from '../../components/forms/Input';
import Button from '../../components/ui/Button';
import apiService from '../../services/apiService';
import './OrdenCompraForm.css';

const OrdenCompraForm = ({ orden, proveedores, onSubmit, onCancel, isLoading, isEdit = false }) => {
    const [formData, setFormData] = useState({
        proveedor_id: '',
        notas: '',
        detalles: []
    });

    const [errors, setErrors] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedResultIndex, setSelectedResultIndex] = useState(0);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Estado para select de proveedor con buscador
    const [showProveedorDropdown, setShowProveedorDropdown] = useState(false);
    const [proveedorSearch, setProveedorSearch] = useState('');
    const proveedorDropdownRef = useRef(null);

    // Cargar datos de la orden si estamos editando
    useEffect(() => {
        if (isEdit && orden) {
            setFormData({
                proveedor_id: orden.proveedor_id || '',
                notas: orden.notas || '',
                detalles: orden.detalles?.map(d => ({
                    producto_id: d.producto_id,
                    producto_nombre: d.producto_nombre,
                    cantidad: parseFloat(d.cantidad)
                })) || []
            });
        }
    }, [isEdit, orden]);

    // Cerrar dropdown de proveedor al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (proveedorDropdownRef.current && !proveedorDropdownRef.current.contains(event.target)) {
                setShowProveedorDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Búsqueda de productos con debounce
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchQuery.trim().length >= 2) {
            searchTimeoutRef.current = setTimeout(async () => {
                try {
                    const result = await apiService.buscarProductos(searchQuery.trim());
                    if (result.success) {
                        setSearchResults(result.data || []);
                        setShowResults(true);
                        setSelectedResultIndex(0);
                    }
                } catch (error) {
                    console.error('Error buscando productos:', error);
                }
            }, 300);
        } else {
            setSearchResults([]);
            setShowResults(false);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Keyboard navigation for search results
    const handleSearchKeyDown = (e) => {
        if (!showResults || searchResults.length === 0) {
            if (e.key === 'Escape') {
                setShowResults(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedResultIndex(prev =>
                    prev < searchResults.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedResultIndex(prev => prev > 0 ? prev - 1 : 0);
                break;
            case 'Enter':
                e.preventDefault();
                if (searchResults[selectedResultIndex]) {
                    handleAddProduct(searchResults[selectedResultIndex]);
                }
                break;
            case 'Escape':
                setShowResults(false);
                break;
            default:
                break;
        }
    };

    const handleAddProduct = (producto) => {
        // Verificar si ya existe en la lista
        const existingIndex = formData.detalles.findIndex(
            d => d.producto_id === producto.id
        );

        if (existingIndex >= 0) {
            // Incrementar cantidad si ya existe
            const newDetalles = [...formData.detalles];
            newDetalles[existingIndex].cantidad += 1;
            setFormData(prev => ({ ...prev, detalles: newDetalles }));
        } else {
            // Agregar nuevo producto (sin precio, se captura al recibir)
            const newDetalle = {
                producto_id: producto.id,
                producto_nombre: producto.nombre,
                cantidad: 1
            };
            setFormData(prev => ({
                ...prev,
                detalles: [...prev.detalles, newDetalle]
            }));
        }

        // Limpiar búsqueda
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
        searchInputRef.current?.focus();
    };

    const handleRemoveProduct = (index) => {
        const newDetalles = formData.detalles.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, detalles: newDetalles }));
    };

    const handleQuantityChange = (index, newQuantity) => {
        const quantity = parseFloat(newQuantity);
        if (isNaN(quantity) || quantity < 0) return;

        const newDetalles = [...formData.detalles];
        newDetalles[index].cantidad = quantity;
        setFormData(prev => ({ ...prev, detalles: newDetalles }));
    };

    const getTotalProductos = () => {
        return formData.detalles.reduce((sum, d) => sum + d.cantidad, 0);
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.proveedor_id) {
            newErrors.proveedor_id = 'Debe seleccionar un proveedor';
        }

        if (formData.detalles.length === 0) {
            newErrors.detalles = 'Debe agregar al menos un producto';
        }

        // Validar cantidades
        formData.detalles.forEach((detalle, index) => {
            if (detalle.cantidad <= 0) {
                newErrors[`cantidad_${index}`] = 'Cantidad inválida';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Sin precios - se capturan al recibir la orden
        const dataToSubmit = {
            proveedor_id: parseInt(formData.proveedor_id),
            notas: formData.notas.trim(),
            subtotal: 0,
            impuesto: 0,
            total: 0,
            detalles: formData.detalles.map(d => ({
                producto_id: d.producto_id,
                cantidad: d.cantidad,
                precio_unitario: 0,
                subtotal: 0
            }))
        };

        onSubmit(dataToSubmit);
    };

    const proveedoresActivos = proveedores.filter(p => p.activo);

    // Filtrar proveedores para el buscador
    const filteredProveedores = proveedoresActivos.filter(p =>
        p.nombre?.toLowerCase().includes(proveedorSearch.toLowerCase())
    );

    // Obtener nombre del proveedor seleccionado
    const getProveedorNombre = () => {
        if (!formData.proveedor_id) return 'Seleccionar proveedor...';
        const prov = proveedoresActivos.find(p => p.id === parseInt(formData.proveedor_id));
        return prov?.nombre || 'Seleccionar proveedor...';
    };

    const handleSelectProveedor = (id) => {
        setFormData(prev => ({ ...prev, proveedor_id: id }));
        setShowProveedorDropdown(false);
        setProveedorSearch('');
        if (errors.proveedor_id) {
            setErrors(prev => ({ ...prev, proveedor_id: '' }));
        }
    };

    return (
        <form onSubmit={handleSubmit} className="orden-compra-form">
            {/* Selector de Proveedor */}
            <div className="form-section">
                <h3 className="section-title">
                    <Truck size={18} />
                    Proveedor
                </h3>
                <div className="form-group">
                    <div className="proveedor-select-container" ref={proveedorDropdownRef}>
                        <button
                            type="button"
                            className={`proveedor-select-btn ${errors.proveedor_id ? 'error' : ''} ${formData.proveedor_id ? 'has-value' : ''}`}
                            onClick={() => !isLoading && setShowProveedorDropdown(!showProveedorDropdown)}
                            disabled={isLoading}
                        >
                            <Truck size={16} />
                            <span>{getProveedorNombre()}</span>
                            <ChevronDown size={16} className={showProveedorDropdown ? 'rotated' : ''} />
                        </button>
                        {showProveedorDropdown && (
                            <div className="proveedor-dropdown">
                                <div className="dropdown-search">
                                    <Search size={16} />
                                    <input
                                        type="text"
                                        placeholder="Buscar proveedor..."
                                        value={proveedorSearch}
                                        onChange={(e) => setProveedorSearch(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className="dropdown-options">
                                    {filteredProveedores.map(prov => (
                                        <div
                                            key={prov.id}
                                            className={`dropdown-option ${formData.proveedor_id === prov.id.toString() || formData.proveedor_id === prov.id ? 'selected' : ''}`}
                                            onClick={() => handleSelectProveedor(prov.id)}
                                        >
                                            {prov.nombre}
                                        </div>
                                    ))}
                                    {filteredProveedores.length === 0 && proveedorSearch && (
                                        <div className="dropdown-empty">
                                            No se encontraron proveedores
                                        </div>
                                    )}
                                    {filteredProveedores.length === 0 && !proveedorSearch && (
                                        <div className="dropdown-empty">
                                            No hay proveedores activos
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {errors.proveedor_id && (
                        <span className="error-message">{errors.proveedor_id}</span>
                    )}
                </div>
            </div>

            {/* Búsqueda de Productos */}
            <div className="form-section">
                <h3 className="section-title">
                    <Package size={18} />
                    Productos
                </h3>

                <div className="product-search-container">
                    <div className="search-input-wrapper">
                        <Input
                            ref={searchInputRef}
                            placeholder="Buscar producto por nombre o código..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            onBlur={() => setTimeout(() => setShowResults(false), 200)}
                            onFocus={() => searchResults.length > 0 && setShowResults(true)}
                            icon={<Search size={18} />}
                            disabled={isLoading}
                        />

                        {showResults && searchResults.length > 0 && (
                            <div className="search-results-dropdown">
                                {searchResults.map((producto, index) => (
                                    <div
                                        key={producto.id}
                                        className={`search-result-item ${index === selectedResultIndex ? 'selected' : ''}`}
                                        onClick={() => handleAddProduct(producto)}
                                    >
                                        <div className="result-info">
                                            <span className="result-name">{producto.nombre}</span>
                                            <span className="result-code">{producto.codigo || producto.codigo_corto}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {errors.detalles && (
                    <span className="error-message">{errors.detalles}</span>
                )}

                {/* Tabla de productos agregados */}
                {formData.detalles.length > 0 && (
                    <div className="productos-agregados">
                        <table className="productos-table">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th style={{ width: '150px', textAlign: 'center' }}>Cantidad</th>
                                    <th style={{ width: '50px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.detalles.map((detalle, index) => (
                                    <tr key={index}>
                                        <td>{detalle.producto_nombre}</td>
                                        <td>
                                            <div className="quantity-controls">
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, detalle.cantidad - 1)}
                                                    disabled={detalle.cantidad <= 1 || isLoading}
                                                    className="qty-btn"
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <input
                                                    type="number"
                                                    value={detalle.cantidad}
                                                    onChange={(e) => handleQuantityChange(index, e.target.value)}
                                                    className="qty-input"
                                                    min="1"
                                                    step="1"
                                                    disabled={isLoading}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleQuantityChange(index, detalle.cantidad + 1)}
                                                    disabled={isLoading}
                                                    className="qty-btn"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveProduct(index)}
                                                className="remove-btn"
                                                disabled={isLoading}
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="productos-resumen">
                            <span>Total: {formData.detalles.length} producto(s), {getTotalProductos()} unidades</span>
                        </div>
                    </div>
                )}

                {formData.detalles.length === 0 && (
                    <div className="empty-products">
                        <Package size={32} />
                        <p>No hay productos agregados</p>
                        <small>Busca y agrega productos usando el campo de arriba</small>
                    </div>
                )}
            </div>

            {/* Notas */}
            <div className="form-section">
                <div className="form-group">
                    <label className="form-label">Notas / Observaciones</label>
                    <textarea
                        value={formData.notas}
                        onChange={(e) => setFormData(prev => ({ ...prev, notas: e.target.value }))}
                        className="form-textarea"
                        placeholder="Notas adicionales para la orden..."
                        rows={3}
                        disabled={isLoading}
                    />
                </div>
            </div>

            {/* Botones de acción */}
            <div className="form-actions">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    Cancelar
                </Button>
                <Button
                    type="submit"
                    loading={isLoading}
                    disabled={isLoading || formData.detalles.length === 0}
                >
                    {isEdit ? 'Actualizar Orden' : 'Crear Orden'}
                </Button>
            </div>
        </form>
    );
};

export default OrdenCompraForm;
